import { Alert, AuthCode, Link, Text } from '@ksyos/design-system';
import { useNavigate } from '@ksyos/router';
import { useState } from 'react';
import { LockStatus } from '../../../../common/enums/lockStatus';
import { TwoFactorAuthenticationType } from '../../../../common/enums/twoFactorAuthenticationType';
import { ValidationErrorCode } from '../../../../common/enums/validationErrorCode';
import { fetchJson } from '../../../store/fetch';
import { AnimatedLock } from '../../elements/AnimatedLock';

interface IMfaCodeInputProps {
    activationToken?: string;
    mfaType?: TwoFactorAuthenticationType;
    onCodeFailed?: () => void;
    onCodeVerified: () => void;
    otpSecret?: string;
    password?: string;
    serial?: string;
    userName?: string;
    wrongCodeCounter?: number;
}
export function MfaCodeInput(props: IMfaCodeInputProps) {
    const navigate = useNavigate();
    const [lockStatus, setLockStatus] = useState<LockStatus>(LockStatus.default);

    return (
        <>
            <AnimatedLock color={lockStatus}></AnimatedLock>
            <Text textAlign="center">
                {props.mfaType === TwoFactorAuthenticationType.verificationDevice
                    ? 'Vul de code op de voorkant van de hardware token in'
                    : 'Vul de code uit je verificatieapp in'}
            </Text>
            <AuthCode
                appearance={lockStatus}
                length={6}
                onChange={handleChangeToken}
            />
            {props.mfaType === TwoFactorAuthenticationType.verificationDevice &&
                !!props.wrongCodeCounter &&
                props.wrongCodeCounter >= 3 && (
                    <Alert appearance="warning">
                        {`Er is mogelijk iets niet goed met gegaan met het registreren van de hardware token. Het door jou ingevoerde serienummer (${formatSerialNumber(
                            props.serial
                        )}) klopt wellicht niet. Als dat zo is, ga naar het vorige scherm en pas het aan.`}
                    </Alert>
                )}

            <Text
                size="sm"
                textAlign="center"
            >
                <Link
                    color="minimal"
                    fontWeight="normal"
                    href={'https://www.ksyos.nl/contact/faq'}
                    openInNewTab={true}
                    variant="externalLink"
                >
                    {props.mfaType === TwoFactorAuthenticationType.verificationDevice
                        ? 'Problemen met de hardware token of code?'
                        : 'Problemen met de app of code?'}
                </Link>
            </Text>
        </>
    );

    function formatSerialNumber(serial: string | undefined) {
        if (!serial) {
            return serial;
        }

        return serial.slice(0, 3) + ' ' + serial.slice(3, 6) + ' ' + serial.slice(6, 9) + ' ' + serial.slice(9);
    }

    async function handleChangeToken(token: string) {
        if (token.length === 6) {
            const response = await fetchJson(
                '/api/practitioners/validateMfa',
                'POST',
                {
                    otpSecret: props.otpSecret,
                    totpToken: token,
                    activationToken: props.activationToken,
                    userName: props.userName,
                    password: props.password,
                    serialNumber: props.serial,
                },
                [
                    ValidationErrorCode.OTP_SECRET_DOES_NOT_MATCH_TOTP_TOKEN,
                    ValidationErrorCode.USER_BLOCKED_TEMPORARILY,
                    ValidationErrorCode.USER_DOES_NOT_MATCH_TOKEN,
                    ValidationErrorCode.ACTIVATION_TOKEN_ALREADY_CONSUMED,
                    ValidationErrorCode.USER_IS_NOT_ACTIVATED,
                    ValidationErrorCode.ACTIVATION_TOKEN_EXPIRED,
                ]
            );

            let result: LockStatus;
            if (response.errorCode === ValidationErrorCode.OTP_SECRET_DOES_NOT_MATCH_TOTP_TOKEN) {
                result = LockStatus.danger;
            } else if (
                response.errorCode &&
                [
                    ValidationErrorCode.USER_BLOCKED_TEMPORARILY,
                    ValidationErrorCode.USER_DOES_NOT_MATCH_TOKEN,
                    ValidationErrorCode.ACTIVATION_TOKEN_ALREADY_CONSUMED,
                    ValidationErrorCode.USER_IS_NOT_ACTIVATED,
                    ValidationErrorCode.ACTIVATION_TOKEN_EXPIRED,
                ].find((er) => er === response.errorCode)
            ) {
                result = LockStatus.danger;
                navigate('/home');
            } else {
                result = response.json.mfaSuccess ? LockStatus.success : LockStatus.danger;
            }

            setLockStatus(result);

            if (result === LockStatus.success) {
                props.onCodeVerified();
            } else {
                if (props.onCodeFailed) {
                    props.onCodeFailed();
                }
                setTimeout(() => {
                    setLockStatus(LockStatus.default);
                }, 2000);
            }
        }
    }
}
